import dayjs, { dateDisplayFormat } from '~libs/dayjs';

import { Column } from '~components/table-view';
import OpenRecordAction from '~support-at-home/components/config/service-cutover/OpenRecordAction';

/**
 * Step 4 of the service cutover assistant - the client budgets holding a line for one of the services
 * being cut over past the cutover date.
 *
 * "Lines" separates the two shapes of line the assistant meets, because they read differently to a
 * provider. A line that starts before the cutover is SPLIT - it keeps its earlier portion and gives
 * up the rest. A line that starts on or after it has no earlier portion to keep, so it MOVES whole.
 *
 * The reason column carries the only thing that stops a budget being brought into line, and is
 * shown rather than the row being dropped: a provider has to know what was left behind. It is only
 * present when something actually is blocked, which is the exception rather than the rule - an
 * always-empty column costs width on every run, and the width it costs comes off the client name.
 */
// Every column is given a width, and they are sized to total less than the modal's inner width.
// TableView lays out on `auto`, so a set of columns that does not fit makes the table wider than
// its card rather than reflowing, and the last column is simply clipped - which here would hide the
// one thing a provider has to be able to read, the reason a budget cannot be moved.
const REFERENCE_WIDTH = '100px';
const CLIENT_WIDTH = '125px';
/** What the client name grows to once the reason column is not there to pay for. */
const CLIENT_WIDTH_WIDE = '250px';
const DATES_WIDTH = '165px';
const STATUS_WIDTH = '75px';
const LINES_WIDTH = '90px';
const REASON_WIDTH = '190px';
const OPEN_WIDTH = '60px';

/**
 * @param {boolean} showBlocked - whether any budget in the whole set cannot be moved. Taken from the
 *   server's count across every page, not from the rows on screen: a provider on page 1 of 101 would
 *   otherwise lose the column the moment their page happened to hold no blocked budget.
 */
const budgetColumns = (showBlocked: boolean): Column[] => [
  {
    id: 'reference',
    header: 'Reference',
    caption: 'The budget reference',
    type: 'text',
    style: { width: REFERENCE_WIDTH },
  },
  {
    id: 'client_name',
    header: 'Client',
    caption: 'The client the budget belongs to',
    type: 'text',
    style: { width: showBlocked ? CLIENT_WIDTH : CLIENT_WIDTH_WIDE },
    classNames: { column: 'sah-cutover-wrap-cell' },
  },
  {
    id: 'dates',
    header: 'Budget Dates',
    caption: 'The period the budget runs over',
    type: 'text',
    style: { width: DATES_WIDTH },
    deriveValue: (row: any) => {
      const startDate = row.start_date ? dayjs(row.start_date).format(dateDisplayFormat) : 'N/A';
      const endDate = row.end_date ? dayjs(row.end_date).format(dateDisplayFormat) : 'N/A';
      return `${startDate} - ${endDate}`;
    },
  },
  {
    id: 'status',
    header: 'Status',
    caption: 'The budget status',
    type: 'text',
    style: { width: STATUS_WIDTH },
  },
  {
    id: 'lines',
    header: 'Lines',
    caption: 'How many lines are split at the cutover, and how many move across whole',
    type: 'text',
    style: { width: LINES_WIDTH },
    deriveValue: (row: any) => {
      const parts = [];
      if (row.split_count > 0) parts.push(`${row.split_count} split`);
      if (row.move_count > 0) parts.push(`${row.move_count} moved`);
      return parts.join(', ');
    },
  },
  ...(showBlocked
    ? [
        {
          id: 'blocked_reason',
          header: 'Cannot be moved',
          caption: 'Why this budget cannot be brought into line',
          type: 'text' as const,
          style: { width: REASON_WIDTH },
          classNames: { column: 'sah-cutover-wrap-cell' },
        },
      ]
    : []),
  {
    id: 'open',
    header: '',
    caption: 'Open this budget in a new tab',
    type: 'custom',
    style: { width: OPEN_WIDTH },
    renderReadOnly: (_value, row: any) =>
      OpenRecordAction({
        id: `cutover-open-budget-${row?.budget_id}`,
        url: `/support-at-home/budgets/${row?.budget_id}`,
        label: 'Open budget',
      }),
  },
];

export default budgetColumns;
